import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import AuthorCard from "../components/AuthorCard";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://novalean-consulting.fr";

function renderBlocks(body) {
  const blocks = [];
  let list = [];
  const flush = () => { if (list.length) { blocks.push({ type: "ul", items: list }); list = []; } };
  body.split("\n").forEach((line) => {
    const t = line.trim();
    if (t.startsWith("## ")) { flush(); blocks.push({ type: "h2", text: t.slice(3) }); }
    else if (t.startsWith("- ")) { list.push(t.slice(2)); }
    else if (t) { flush(); blocks.push({ type: "p", text: t }); }
    else flush();
  });
  flush();
  return blocks;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    axios.get(`${API}/articles/${slug}`)
      .then(({ data }) => { setArticle(data); setState("ok"); })
      .catch(() => setState("missing"));
  }, [slug]);

  if (state === "loading") return <div className="min-h-screen bg-ink-950" data-testid="article-loading" />;
  if (state === "missing") {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center px-6 text-center" data-testid="article-not-found">
        <h1 className="font-display text-4xl font-semibold">Article introuvable</h1>
        <p className="text-ink/55 mt-4">Cet article n'existe pas ou n'est plus publié.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 mt-8 label-mono text-ink link-underline" data-testid="article-back-404">
          <ArrowLeft size={15} /> Retour au blog
        </Link>
      </div>
    );
  }

  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.published_at,
    dateModified: article.updated_at,
    mainEntityOfPage: `${SITE}/blog/${article.slug}`,
    author: { "@type": "Person", name: "Majdi Zarougui", url: `${SITE}/majdi-zarougui` },
    publisher: { "@type": "Organization", name: "NovaLean Consulting", url: SITE },
  }];

  return (
    <>
      <SEO title={`${article.title} | Majdi Zarougui — NovaLean Consulting`}
        description={article.excerpt} path={`/blog/${article.slug}`} jsonLd={jsonLd} />

      <section className="bg-ink-950 text-paper grain relative pt-32 md:pt-40 pb-16 md:pb-20" data-testid="article-hero">
        <div className="absolute -left-24 top-10 w-[380px] h-[380px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative z-10 mx-auto max-w-[900px] px-5 md:px-10">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 label-mono text-paper/50 hover:text-gold transition-colors" data-testid="article-back">
              <ArrowLeft size={15} /> Tous les articles
            </Link>
            {article.category && <span className="label-mono text-gold block mt-8">[ {article.category} ]</span>}
            <h1 className="font-display text-4xl md:text-6xl font-semibold tracking-tight leading-[1.02] mt-4" data-testid="article-title">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mt-8 pt-7 border-t border-paper/15">
              <Link to="/majdi-zarougui" className="group flex items-center gap-3" data-testid="article-author-link">
                <img src="/images/majdi-zarougui.jpg" alt="Majdi Zarougui" className="w-11 h-11 rounded-full object-cover object-top border border-paper/20" />
                <span>
                  <span className="block font-display font-semibold group-hover:text-gold transition-colors">Majdi Zarougui</span>
                  <span className="block label-mono text-paper/45 mt-0.5">Expert Industrialisation & Excellence Opérationnelle</span>
                </span>
              </Link>
              <span className="label-mono text-paper/45" data-testid="article-meta">
                {formatDate(article.published_at)} · {article.read_time} min de lecture
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper py-16 md:py-24" data-testid="article-body">
        <div className="mx-auto max-w-[760px] px-5 md:px-10">
          {renderBlocks(article.body).map((b, i) => {
            if (b.type === "h2") return <h2 key={i} className="font-display text-2xl md:text-3xl font-semibold mt-12 mb-5 tracking-tight">{b.text}</h2>;
            if (b.type === "ul") return (
              <ul key={i} className="space-y-2.5 my-6">
                {b.items.map((it, j) => (
                  <li key={`${i}-${j}`} className="flex items-start gap-3 text-ink/70 leading-relaxed">
                    <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-gold shrink-0" />{it}
                  </li>
                ))}
              </ul>
            );
            return <p key={i} className="text-ink/70 text-lg leading-relaxed my-6">{b.text}</p>;
          })}
        </div>
      </section>

      <section className="bg-paper pb-28">
        <div className="mx-auto max-w-[760px] px-5 md:px-10 space-y-10">
          <AuthorCard />
          <Reveal>
            <Link to="/majdi-zarougui" className="group inline-flex items-center gap-2 label-mono text-ink link-underline" data-testid="article-profile-cta">
              Découvrir toutes les expertises de Majdi <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
